import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Cap on the bootstrap flag fetch. The APP_INITIALIZER awaits load(); without this, a stalled
 * connection (never resolves, never errors) would hang the whole app boot. On timeout we fall
 * through to the safe defaults (every flag false), so a slow network degrades, never blocks.
 */
const FLAG_LOAD_TIMEOUT_MS = 5000;

/** The bare camelCase shape the #77 feature-flags endpoint returns (also tolerated under `result`). */
interface FeatureFlagsResponse {
  knowledgeCentreEnabled?: boolean;
  askAiEnabled?: boolean;
  homepageStatsCmsEnabled?: boolean;
  notesEnabled?: boolean;
}

/**
 * Holds the platform feature flags the app reads at bootstrap (issue #77). The flags:
 *  - `knowledgeCentreEnabled` — the Learn cutover: ON → `/learn` resolves to the new Knowledge Centre
 *    (the canMatch guard swaps it) and the Learn nav tab leads there; OFF (default) → legacy portal.
 *  - `askAiEnabled` — the customer-facing grounded AI answer (#50) in the Knowledge Centre search.
 *    OFF (default) → the AI answer is suppressed (the API also returns none); keyword search remains.
 *  - `homepageStatsCmsEnabled` — read for completeness; the homepage figures are gated server-side by
 *    this flag (the API serves static figures when OFF), so the SPA needs no extra branching for it.
 *
 * Values are populated ONCE at startup by an APP_INITIALIZER (see app.module) that GETs the public,
 * anonymous feature-flags endpoint on the .NET API. Every flag DEFAULTS TO FALSE and falls back to
 * false on ANY error (API slow/down/non-2xx) — so a degraded API can only ever land on today's safe
 * behaviour (legacy portal, no AI answer, static stats), never a broken screen.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  /** Learn-cutover flag. Default OFF — today's behaviour — until the bootstrap load flips it. */
  knowledgeCentreEnabled = false;
  /** Grounded AI answer (#50). Default OFF — "off until launch" — until the bootstrap load flips it. */
  askAiEnabled = false;
  /** Homepage figures source (CMS vs static). Default OFF. Enforced server-side; mirrored here. */
  homepageStatsCmsEnabled = false;
  /**
   * P2P-28 / P2P-29 — the P2P Notes launch flag. ON → the P2P sections on the consolidated
   * T&C and risk-warning pages become visible (still marked DRAFT until Legal delivers final
   * copy). Default OFF: nothing P2P is publicly reachable. Owned by P2P-29; read here so
   * P2P-28's page work can ship dark behind it.
   */
  notesEnabled = false;

  constructor(private http: HttpClient) {}

  /**
   * Called once by the APP_INITIALIZER. Fetches the flags from the anonymous public endpoint and
   * stores them. Anonymous call — passes `noToken` like the other knowledge calls so it skips the
   * auth/refresh path. NEVER rejects: any error degrades every flag to the safe default (false), so a
   * failed/slow flag load can't block bootstrap or break a screen.
   */
  async load(): Promise<void> {
    try {
      // The #77 feature-flags endpoint returns a BARE shape — `{ knowledgeCentreEnabled: false, … }`
      // (StaticUIController returns `Ok(...)`, NOT the BaseApiController `{ result }` envelope; the
      // #36 stats/survey endpoints are bare too). Read it directly; also tolerate a `{ result }`
      // wrapper in case the endpoint is ever moved behind Result().
      const data = await firstValueFrom(
        this.http
          .get<FeatureFlagsResponse & { result?: FeatureFlagsResponse }>(
            environment.apiServer + 'Api/v1/StaticUI/public/feature-flags',
            { params: { noToken: 'noToken' } },
          )
          .pipe(timeout(FLAG_LOAD_TIMEOUT_MS)),
      );
      const flags = data?.result ?? data;
      this.knowledgeCentreEnabled = flags?.knowledgeCentreEnabled === true;
      this.askAiEnabled = flags?.askAiEnabled === true;
      this.homepageStatsCmsEnabled = flags?.homepageStatsCmsEnabled === true;
      this.notesEnabled = flags?.notesEnabled === true;
    } catch {
      // Degrade to today's safe behaviour on any failure — never a broken screen.
      this.knowledgeCentreEnabled = false;
      this.askAiEnabled = false;
      this.homepageStatsCmsEnabled = false;
      this.notesEnabled = false;
    }
  }
}
